/**
 * SignInMethodsCard — Settings link/unlink controls (T27.2.10.1 Deliverable 7).
 *
 * Lets a signed-in user manage which methods sign in to their account:
 *   - Set / change a password (the signed-in session proves email ownership,
 *     so no email check — the linking-safety rule).
 *   - Connect Google (points at the existing Google sign-in; the auto-link
 *     happens server-side on a verified-email Google sign-in).
 *   - Unlink a method, blocked when it is the LAST remaining identity (the
 *     server enforces this too; the control explains why when blocked).
 *
 * Token-only styling (CP5), reusing the settings `setgroup` card vocabulary.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  useAuthIdentities,
  useSetPassword,
  useUnlinkIdentity,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import { Button, Card, Input } from '../../components';
import {
  sectionHeaderStyle,
  setgroupCardPaddedStyle,
} from './cards';

const MIN_PASSWORD_LENGTH = 8;

export function SignInMethodsCard(): JSX.Element {
  const identitiesQuery = useAuthIdentities();
  const setPassword = useSetPassword();
  const unlink = useUnlinkIdentity();

  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);

  const data = identitiesQuery.data;
  const hasPassword = data?.hasPassword ?? false;
  const hasGoogle = data?.hasGoogle ?? false;
  const methodCount = (hasPassword ? 1 : 0) + (hasGoogle ? 1 : 0);
  const isLastMethod = methodCount <= 1;

  const handleSetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }
    try {
      await setPassword.mutateAsync({ password });
      setPasswordValue('');
      setConfirm('');
      setFormSuccess(hasPassword ? 'Password updated.' : 'Password set.');
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Could not set password.'));
    }
  };

  const handleUnlink = async (provider: 'password' | 'google') => {
    setUnlinkError(null);
    const label = provider === 'google' ? 'Google' : 'password';
    const ok = window.confirm(`Remove ${label} sign-in from your account?`);
    if (!ok) return;
    try {
      await unlink.mutateAsync(provider);
    } catch (err) {
      setUnlinkError(apiErrorMessage(err, `Could not remove ${label} sign-in.`));
    }
  };

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-signin-methods"
    >
      <h3 style={sectionHeaderStyle}>Sign-in methods</h3>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-1)' }}>
        Add a second way to sign in so you always have a backup. You need at
        least one.
      </p>

      {identitiesQuery.isLoading && (
        <p style={{ marginTop: 'var(--space-3)' }} data-testid="signin-methods-loading">
          Loading…
        </p>
      )}

      {data && (
        <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {/* Google method row */}
          <div data-testid="signin-method-google">
            <div style={{ fontWeight: 'var(--font-weight-medium)' }}>Google</div>
            {hasGoogle ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-1)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-status-success)' }}>
                  Connected
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isLastMethod || unlink.isPending}
                  onClick={() => handleUnlink('google')}
                  data-testid="signin-unlink-google"
                >
                  Remove
                </Button>
              </div>
            ) : (
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-1)' }}>
                Not connected. Sign out and choose “Sign in with Google” on the
                same email to connect it.
              </p>
            )}
          </div>

          {/* Password method row */}
          <div data-testid="signin-method-password">
            <div style={{ fontWeight: 'var(--font-weight-medium)' }}>Password</div>
            {hasPassword && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-1)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-status-success)' }}>
                  Set
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isLastMethod || unlink.isPending}
                  onClick={() => handleUnlink('password')}
                  data-testid="signin-unlink-password"
                >
                  Remove
                </Button>
              </div>
            )}

            <form
              onSubmit={handleSetPassword}
              data-testid="signin-set-password-form"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}
              noValidate
            >
              <Input
                label={hasPassword ? 'New password' : 'Set a password'}
                type="password"
                name="new-password"
                autoComplete="new-password"
                reveal
                value={password}
                onChange={(e) => setPasswordValue(e.target.value)}
                data-testid="signin-password-input"
              />
              <Input
                label="Confirm password"
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                reveal
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="signin-password-confirm"
              />
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={setPassword.isPending}
                  data-testid="signin-set-password-submit"
                >
                  {hasPassword ? 'Change password' : 'Set password'}
                </Button>
              </div>
              {formError && (
                <p role="alert" data-testid="signin-form-error" style={{ color: 'var(--color-status-danger)', fontSize: 'var(--font-size-sm)' }}>
                  {formError}
                </p>
              )}
              {formSuccess && (
                <p role="status" data-testid="signin-form-success" style={{ color: 'var(--color-status-success)', fontSize: 'var(--font-size-sm)' }}>
                  {formSuccess}
                </p>
              )}
            </form>
          </div>

          {isLastMethod && (
            <p data-testid="signin-last-method-note" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              This is your only sign-in method — add another before removing it.
            </p>
          )}

          {unlinkError && (
            <p role="alert" data-testid="signin-unlink-error" style={{ color: 'var(--color-status-danger)', fontSize: 'var(--font-size-sm)' }}>
              {unlinkError}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
